/* eslint-disable react/prop-types */
import React, { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import HeightTransition from '../utils/HeightTransition';
import Icon from '../Icon';

import styles from './Input2.module.scss';

const addSeparator = value => (
  value && value
    .toString()
    .replace(/\D/g, '')
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
);

const Input2 = forwardRef((props, ref) => {
  const {
    id,
    className,
    type,
    placeholder,
    name,
    value,
    resetValue,
    required,
    onChange,
    onBlur,
    onFocus,
    icon,
    size,
    theme,
    error,
    errorMessage,
    errorRequiredMessage,
    numericThousandSeparator,
    fullWidth,
    onKeyPress,
    onEnterPress,
    maxLength,
    autoComplete,
    valid,
    forceError,
    resetStyle,
    search,
    ...rest
  } = props;
  const [focus, setFocus] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [formattedValue, setFormattedValue] = useState(() => addSeparator(value));

  // Manage Error
  const hasError = hasBeenFocused && !focus && error;
  const hasForceError = !focus && forceError;
  const hasRequiredError = error || (hasBeenFocused && required && !focus && !value);
  const handleDisplayError = hasError || hasRequiredError || hasForceError;

  const cnContainer = cn(styles.container, styles[size], styles[theme], className, {
    [styles.fullWidth]: fullWidth,
    [styles.error]: handleDisplayError,
    [styles.reset]: resetStyle,
    [styles.search]: search,
    [styles.valid]: valid
  });

  const handleChange = useCallback(
    e => {
      e.persist();
      const val = e?.target?.value;
      if (onChange) {
        if (!numericThousandSeparator) {
          return onChange({
            value: val,
            name: e?.target?.name,
            id
          });
        }
        setFormattedValue(addSeparator(val));
        return onChange({
          value: val.replace(/\D+/g, ''),
          name: e?.target?.name,
          id,
          event: e
        })
      }
    },
    [id, numericThousandSeparator, onChange]
  );

  const handleKeyPress = useCallback(
    e =>  {
      if (e.key === 'Enter') onEnterPress(e);
      if (onKeyPress && e.key !== 'Enter') onKeyPress(e);
    },
    [onEnterPress, onKeyPress]
  )

  const handleFocus = useCallback(
    async e => {
      e.persist();
      await setFocus(true);
      await setHasBeenFocused(true);
      onFocus({ event: e });
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    e => {
      e.persist();
      setFocus(false);
      onBlur({ event: e });
    },
    [onBlur]
  );

  const renderSearchIcon = useMemo(
    () => {
      if (search) {
        return (
          <Icon
            className={styles.searchIcon}
            label="search"
            width={16}
            fill="#4a739d"
            theme="regular"
          />
        )
      }
      return null;
    },
    [search]
  );

  const renderIcon = useMemo(
    () => {
      const resetInput = () => onChange({ value: '', name: '', id: '' });
      if (icon || value) {
        return (
          <Icon
            className={styles.icon}
            label={value ? 'times' : icon}
            width={16}
            fill="#4a739d"
            theme="regular"
            isButton={!!value}
            onClick={resetInput}
          />
        )
      }
      return null;
    },
    [icon, value, onChange]
  );

  const renderErrorMessage = useMemo(
    () => (
      <HeightTransition config={{
        tension: 340,
        friction: 26,
        mass: 1,
        precision: 1
      }} on={handleDisplayError && (errorMessage || errorRequiredMessage)}>
        <span className={styles.helper}>
          <Icon label="exclamation-circle"/>
          {errorMessage || errorRequiredMessage}
        </span>
      </HeightTransition>
    ),
    [handleDisplayError, errorMessage, errorRequiredMessage]
  );

  return (
    <div className={cnContainer} {...rest}>
      <div className={styles.content}>
        {renderSearchIcon}
        <input
          {...rest}
          ref={ref}
          id={id}
          placeholder={placeholder}
          className={styles.input}
          type={numericThousandSeparator ? 'text' : type}
          name={name}
          value={numericThousandSeparator ? formattedValue : value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          required={required}
          onKeyPress={handleKeyPress}
          maxLength={maxLength}
          autoComplete={autoComplete}
        />
        {renderIcon}
      </div>
      {renderErrorMessage}
    </div>
  );
});

Input2.displayName = 'Input2';

Input2.propTypes = {
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  className: PropTypes.string,
  id: PropTypes.string,
  icon: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  fullWidth: PropTypes.bool,
  resetStyle: PropTypes.bool,
  numericThousandSeparator: PropTypes.bool,
  errorMessage: PropTypes.string,
  type: PropTypes.oneOf([
    'text',
    'email',
    'password',
    'number',
    'tel',
    'search',
    'hidden'
  ]),
  size: PropTypes.oneOf([
    'small',
    'medium',
    'large',
  ]),
  theme: PropTypes.oneOf([
    'primary',
    'secondary',
  ]),
  value: PropTypes.any,
  resetValue: PropTypes.func,
  valid: PropTypes.bool,
  error: PropTypes.bool,
  forceError: PropTypes.bool,
  onKeyPress: PropTypes.func,
  onEnterPress: PropTypes.func,
  autoComplete: PropTypes.oneOf(['on', 'off'])
};

Input2.defaultProps = {
  onChange: null,
  id: undefined,
  className: undefined,
  icon: null,
  name: null,
  type: 'text',
  value: undefined,
  resetValue: () => {},
  size: 'medium',
  theme: 'primary',
  valid: false,
  fullWidth: false,
  resetStyle: false,
  required: false,
  numericThousandSeparator: false,
  error: false,
  forceError: false,
  errorMessage: null,
  onKeyPress: () => {},
  onEnterPress: () => {},
  onFocus: () => {},
  onBlur: () => {},
};

export default memo(Input2);
